import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react'
import { sameSort } from '@/components/data-table/list-state'

/**
 * Record navigation surfaced in the global header (FileMaker-style: position +
 * prev/next/first/last + quick actions). A detail/edit page publishes its config
 * via useSetRecordNav; the header's <RecordNav/> renders it. Cleared when no
 * detail page is mounted.
 *
 * The list filter store lets a list page record its current query input (e.g.
 * search) per entity key, so the detail page can ask the backend for neighbours
 * over the same filtered set ("follow the list").
 */
export interface RecordSearchResult {
  id: string
  label: string
  sublabel?: string
}

export interface RecordNavConfig {
  kind: 'record'
  position: number
  total: number
  goFirst: (() => void) | null
  goPrev: (() => void) | null
  goNext: (() => void) | null
  goLast: (() => void) | null
  goTo: (id: string) => void
  /** Weg voor wie de lijst niet mag zien (het dagoverzicht van de urenregistratie). */
  toList?: () => void
  /** Optional: when omitted, the header shows no "+" (reference-data / read-only entities). */
  create?: () => void
  /** Optional: when omitted, the header shows no delete button (read-only entities). */
  remove?: () => void
  /** In-table record search: returns matches for a term; picking one navigates. */
  searchRecords?: (term: string) => Promise<RecordSearchResult[]>
  /** De zoekmodus: het vergrootglas opent hem, in plaats van `searchRecords`. */
  find?: () => void
  /** Met een gevonden set: haal dit record uit de set. */
  omitFromFound?: () => void
  /** Het snelle zoekveld van een lijst, voor een record dat zelf een lijst is (een dag in de
   *  urenregistratie): het filtert de rijen op het scherm, in plaats van `searchRecords`. */
  onSearch?: (term: string) => void
}

/**
 * Lightweight nav published by overview lists (no current record): the record
 * count, an in-place list filter, and a "new" action. `onSearch` narrows the
 * list on the page (you stay on the overview). Shares the header slot with the
 * full record navigator.
 */
export interface ListNavConfig {
  kind: 'list'
  total: number
  /** Optional: when omitted, the header shows no "+" (read-only lists). */
  create?: () => void
  /** Het snelle zoekveld; weg als het scherm een zoekmodus heeft (`find`). */
  onSearch?: (term: string) => void
  /** De zoekmodus: het vergrootglas opent hem, in plaats van het zoekveld. */
  find?: () => void
}

export type NavConfig = RecordNavConfig | ListNavConfig

export interface ListFilter {
  search?: string
  /** Active overview sort, so the detail's neighbours follow the same order. */
  sort?: { column: string; dir: 'asc' | 'desc' }
  /**
   * De entiteitseigen lijstfilters (product actief/soort, offertestatus, …), zodat de
   * detailpagina ze meestuurt in zijn `neighbors`-aanroep. Zoekterm en sortering reisden
   * al mee, filters niet — die versmalden de lijst maar niet `position / total` of
   * vorige/volgende.
   */
  filters?: Record<string, unknown>
}

export interface LastOpened {
  id: string
  /** 1-gebaseerde positie in de laatst bekende gevonden set (neighbors.position),
   *  zodra de detailpagina die kende. Ontbreekt zolang de buren-query nog niet
   *  is opgelost. */
  position?: number
  /** De lijstsortering waaronder `position` is gemeten (van `useListFilter` op
   *  het moment dat de buren-query oploste). Een lijst die inmiddels anders
   *  sorteert, mag `position` niet vertrouwen — zie `sameSort` in goToPosition. */
  sort?: ListFilter['sort']
}

interface Ctx {
  nav: NavConfig | null
  setNav: (n: NavConfig | null) => void
  filters: Record<string, ListFilter>
  setFilter: (entityKey: string, filter: Partial<ListFilter>) => void
  lastOpened: Record<string, LastOpened>
  setLastOpened: (
    entityKey: string,
    id: string,
    position?: number,
    sort?: ListFilter['sort'],
  ) => void
}

const RecordNavContext = createContext<Ctx>({
  nav: null,
  setNav: () => {},
  filters: {},
  setFilter: () => {},
  lastOpened: {},
  setLastOpened: () => {},
})

export function RecordNavProvider({ children }: { children: ReactNode }) {
  const [nav, setNav] = useState<NavConfig | null>(null)
  const [filters, setFilters] = useState<Record<string, ListFilter>>({})
  const [lastOpened, setLastOpenedState] = useState<Record<string, LastOpened>>({})
  /**
   * SAMENVOEGEN per sleutel, niet vervangen. Eén lijstpagina publiceert namelijk uit twee
   * hoeken naar dezelfde entiteit: `useServerList` stuurt `{search, sort, filters}` en
   * `useEntityListNav` daarna `{search, sort}`. Bij vervangen won de laatste registratie
   * en wiste die de entiteitsfilters meteen weer — de detailpagina las dan wel `filters`,
   * maar kreeg een leeg object, dus `position / total` en vorige/volgende liepen over de
   * ONGEFILTERDE set terwijl de lijst gefilterd was. Compileerde prima, telde verkeerd.
   *
   * Een sleutel die wél in het patch-object staat overschrijft nog steeds, ook met
   * `undefined` — zo blijft een gewiste zoekterm of een leeggemaakt filter wissen.
   */
  const setFilter = useCallback((entityKey: string, filter: Partial<ListFilter>) => {
    setFilters((prev) => {
      const merged = { ...prev[entityKey], ...filter }
      const cur = prev[entityKey]
      if (
        cur &&
        cur.search === merged.search &&
        sameSort(cur.sort, merged.sort) &&
        JSON.stringify(cur.filters ?? {}) === JSON.stringify(merged.filters ?? {})
      ) {
        return prev
      }
      return { ...prev, [entityKey]: merged }
    })
  }, [])
  const setLastOpened = useCallback(
    (entityKey: string, id: string, position?: number, sort?: ListFilter['sort']) => {
      setLastOpenedState((prev) => {
        const cur = prev[entityKey]
        if (cur && cur.id === id && cur.position === position && sameSort(cur.sort, sort)) {
          return prev
        }
        return { ...prev, [entityKey]: { id, position, sort } }
      })
    },
    [],
  )
  return (
    <RecordNavContext.Provider
      value={{ nav, setNav, filters, setFilter, lastOpened, setLastOpened }}
    >
      {children}
    </RecordNavContext.Provider>
  )
}

export function useRecordNav() {
  return useContext(RecordNavContext)
}

/**
 * Publish the current page's record-nav config to the header. Re-publishes when
 * the meaningful state (position/total/which arrows are enabled) changes; the
 * handler closures are read fresh on each such change. Clears on unmount.
 */
export function useSetRecordNav(config: NavConfig | null) {
  const { setNav } = useRecordNav()
  const key = config
    ? config.kind === 'list'
      ? `list/${config.total}`
      : `${config.position}/${config.total}/${+!!config.goFirst}${+!!config.goPrev}${+!!config.goNext}${+!!config.goLast}${+!!config.toList}`
    : 'none'
  useEffect(() => {
    setNav(config)
    return () => setNav(null)
  }, [key, setNav])
}

/** Read + set the stored list filter for an entity key. */
export function useListFilter(entityKey: string) {
  const { filters, setFilter } = useRecordNav()
  return {
    filter: filters[entityKey] ?? {},
    setFilter: useCallback((f: ListFilter) => setFilter(entityKey, f), [entityKey, setFilter]),
  }
}

/** Read + set the last-opened record id (+ its list position/sort, if known)
 *  for an entity key. A list highlights `lastId` and, when it isn't on the
 *  loaded page, jumps to `lastPosition` (see `pageIndexFor` / `goToPosition`).
 *  `lastSort` is the sort `lastPosition` was measured under — `goToPosition`
 *  refuses the jump when the list's current sort no longer matches it. */
export function useLastOpened(entityKey: string) {
  const { lastOpened, setLastOpened } = useRecordNav()
  const entry = lastOpened[entityKey]
  return {
    lastId: entry?.id ?? null,
    lastPosition: entry?.position ?? null,
    lastSort: entry?.sort ?? null,
    setLastOpened: useCallback(
      (id: string, position?: number, sort?: ListFilter['sort']) =>
        setLastOpened(entityKey, id, position, sort),
      [entityKey, setLastOpened],
    ),
  }
}
